import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Shield, Phone, MapPin, AlertTriangle, X, Plus, Trash2, Navigation, Loader, Bell, PhoneCall } from 'lucide-react';

interface SafetyCompanionProps {
  userId: string | null;
  userLocation: { lat: number; lng: number } | null;
  tier: string;
  onClose: () => void;
  onUpgrade: () => void;
}

interface Contact {
  name: string;
  phone: string;
  relation: string;
}

interface ThreatInfo {
  eid: string;
  type: string;
  address: string;
  level: string;
  minutes_ago: number;
  active: boolean;
}

export default function SafetyCompanion({ userId, userLocation, tier, onClose, onUpgrade }: SafetyCompanionProps) {
  const [tab, setTab] = useState<'monitor' | 'contacts' | 'sos'>('monitor');
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [newContact, setNewContact] = useState({ name: '', phone: '', relation: 'emergency' });
  const [threats, setThreats] = useState<ThreatInfo[]>([]);
  const [analysis, setAnalysis] = useState<any>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [session, setSession] = useState<number | null>(null);
  const [sosTriggered, setSosTriggered] = useState(false);
  const [sosResult, setSosResult] = useState<any>(null);
  const [shakeEnabled, setShakeEnabled] = useState(false);
  const shakeCountRef = useRef(0);
  const lastShakeRef = useRef(0);

  const isPaid = tier === 'tactical' || tier === 'ghost';

  // Load contacts on mount
  useEffect(() => {
    if (!userId) return;
    fetch(`/scanner-api/safety/contacts?userId=${userId}`)
      .then(r => r.json())
      .then(d => setContacts(d.contacts || []))
      .catch(() => {});
  }, [userId]);

  // Load nearby threats
  useEffect(() => {
    if (!userLocation) return;
    fetch(`/scanner-api/safety/nearby-threats?lat=${userLocation.lat}&lon=${userLocation.lng}&radius=2`)
      .then(r => r.json())
      .then(d => setThreats(d.threats || []))
      .catch(() => {});

    const interval = setInterval(() => {
      fetch(`/scanner-api/safety/nearby-threats?lat=${userLocation.lat}&lon=${userLocation.lng}&radius=2`)
        .then(r => r.json())
        .then(d => setThreats(d.threats || []))
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [userLocation]);

  // Shake-to-SOS detection
  useEffect(() => {
    if (!shakeEnabled || !isPaid) return;

    const handleMotion = (e: DeviceMotionEvent) => {
      const acc = e.accelerationIncludingGravity;
      if (!acc) return;
      const force = Math.sqrt((acc.x || 0) ** 2 + (acc.y || 0) ** 2 + (acc.z || 0) ** 2);
      const now = Date.now();

      if (force > 25) { // Strong shake
        if (now - lastShakeRef.current < 1000) {
          shakeCountRef.current++;
          if (shakeCountRef.current >= 3) { // 3 shakes in 1 second
            triggerSOS('shake');
            shakeCountRef.current = 0;
          }
        } else {
          shakeCountRef.current = 1;
        }
        lastShakeRef.current = now;
      }
    };

    window.addEventListener('devicemotion', handleMotion);
    return () => window.removeEventListener('devicemotion', handleMotion);
  }, [shakeEnabled, isPaid]);

  const triggerSOS = useCallback(async (trigger = 'manual') => {
    if (!userId) return;
    setSosTriggered(true);
    try {
      const r = await fetch('/scanner-api/safety/sos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId, trigger,
          lat: userLocation?.lat,
          lon: userLocation?.lng,
        }),
      });
      const data = await r.json();
      setSosResult(data);

      // Vibrate pattern for SOS
      if (navigator.vibrate) navigator.vibrate([200, 100, 200, 100, 200]);
    } catch {
      setSosResult({ message: 'SOS sent. Check your contacts.' });
    }
  }, [userId, userLocation]);

  const startSession = async () => {
    if (!userId || !userLocation) return;
    setAnalyzing(true);
    try {
      const r = await fetch('/scanner-api/safety/start-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId, type: 'walk-home',
          lat: userLocation.lat, lon: userLocation.lng,
        }),
      });
      const data = await r.json();
      setAnalysis(data.analysis);
      setSession(data.sessionId);
    } catch {
      setAnalysis({ safe: true, analysis: 'Could not analyze route.' });
    }
    setAnalyzing(false);
  };

  const saveContact = async () => {
    if (!userId || !newContact.name || !newContact.phone) return;
    await fetch('/scanner-api/safety/contacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId, contacts: [newContact] }),
    });
    setContacts(prev => [...prev, newContact]);
    setNewContact({ name: '', phone: '', relation: 'emergency' });
  };

  if (!isPaid) {
    return (
      <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-[#110a1c] rounded-2xl border border-amber-900/30 p-8 text-center">
          <Shield size={48} className="text-amber-500 mx-auto mb-4" />
          <h2 className="font-orbitron text-xl text-amber-400 mb-2">AI SAFETY COMPANION</h2>
          <p className="text-amber-700 text-sm mb-6 font-mono">
            24/7 AI-powered safety monitoring. Route analysis. Shake-to-SOS.
            Emergency contact alerts. Distress detection.
          </p>
          <p className="text-amber-500 text-lg font-bold mb-4">Included with TACTICAL — $9.99/mo</p>
          <p className="text-amber-800 text-xs mb-4 font-mono">Citizen charges $19.99/mo for the same thing with human agents. Our AI is faster.</p>
          <button onClick={onUpgrade} className="w-full bg-orange-500 text-black font-bold py-3 rounded-xl font-mono uppercase mb-3">
            Upgrade to Tactical
          </button>
          <button onClick={onClose} className="text-amber-800 text-xs font-mono">Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/95 backdrop-blur-md overflow-y-auto">
      <div className="min-h-full flex flex-col items-center py-6 px-4">
        <div className="w-full max-w-md">

          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-emerald-900/30 border border-emerald-500/40 flex items-center justify-center">
                <Shield size={20} className="text-emerald-400" />
              </div>
              <div>
                <h2 className="font-orbitron text-sm text-emerald-400 tracking-widest">SAFETY COMPANION</h2>
                <p className="text-emerald-700 text-[10px] font-mono">AI-POWERED • 24/7 • ACTIVE</p>
              </div>
            </div>
            <button onClick={onClose} className="text-amber-700 hover:text-amber-400"><X size={20} /></button>
          </div>

          {/* SOS Button — Always visible */}
          <button
            onClick={() => triggerSOS('manual')}
            disabled={sosTriggered}
            className={`w-full py-4 rounded-xl font-orbitron text-lg tracking-widest mb-4 transition-all ${
              sosTriggered
                ? 'bg-red-900/50 border-2 border-red-500 text-red-400 animate-pulse'
                : 'bg-red-600 hover:bg-red-500 text-white border-2 border-red-500 shadow-[0_0_30px_rgba(239,68,68,0.3)]'
            }`}
          >
            {sosTriggered ? '🚨 SOS SENT' : '🚨 EMERGENCY SOS'}
          </button>

          {sosResult && (
            <div className="bg-red-900/20 border border-red-500/30 rounded-lg p-3 mb-4 text-red-400 text-xs font-mono">
              {sosResult.message}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            {[
              { id: 'monitor' as const, label: 'MONITOR', icon: <Navigation size={14} /> },
              { id: 'contacts' as const, label: 'CONTACTS', icon: <Phone size={14} /> },
              { id: 'sos' as const, label: 'SETTINGS', icon: <Bell size={14} /> },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`flex-1 py-2 rounded-lg text-[10px] font-mono uppercase tracking-widest flex items-center justify-center gap-1 transition-all ${
                  tab === t.id ? 'bg-emerald-900/30 border border-emerald-500/40 text-emerald-400' : 'border border-transparent text-amber-800 hover:text-amber-600'
                }`}
              >
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {/* Monitor Tab */}
          {tab === 'monitor' && (
            <div className="space-y-4">
              {/* Walk Home button */}
              <button
                onClick={startSession}
                disabled={analyzing || !userLocation}
                className="w-full py-3 bg-emerald-900/20 border border-emerald-500/30 rounded-xl text-emerald-400 font-mono text-sm hover:bg-emerald-900/30 flex items-center justify-center gap-2"
              >
                {analyzing ? <><Loader size={16} className="animate-spin" /> ANALYZING ROUTE...</> : <><Navigation size={16} /> I'M WALKING HOME</>}
              </button>

              {/* AI Analysis */}
              {analysis && (
                <div className={`p-4 rounded-xl border ${
                  analysis.level === 'avoid' ? 'bg-red-900/20 border-red-500/30' :
                  analysis.level === 'caution' ? 'bg-amber-900/20 border-amber-500/30' :
                  'bg-emerald-900/20 border-emerald-500/30'
                }`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-2 h-2 rounded-full ${
                      analysis.level === 'avoid' ? 'bg-red-500' : analysis.level === 'caution' ? 'bg-amber-500' : 'bg-emerald-500'
                    }`} />
                    <span className="font-mono text-xs uppercase tracking-widest text-amber-400">
                      {analysis.level === 'avoid' ? 'AVOID AREA' : analysis.level === 'caution' ? 'USE CAUTION' : 'ROUTE CLEAR'}
                    </span>
                  </div>
                  <p className="text-amber-600 text-xs font-mono leading-relaxed">{analysis.analysis}</p>
                  {analysis.tip && <p className="text-emerald-500 text-xs font-mono mt-2">💡 {analysis.tip}</p>}
                </div>
              )}

              {/* Nearby Threats */}
              <div>
                <h3 className="text-amber-700 text-[10px] font-mono uppercase tracking-widest mb-2">
                  NEARBY ACTIVITY ({threats.filter(t => t.active).length} active)
                </h3>
                {threats.length === 0 ? (
                  <p className="text-emerald-600 text-xs font-mono">✓ No active threats nearby</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {threats.slice(0, 10).map(t => (
                      <div key={t.eid} className={`flex items-start gap-2 p-2 rounded-lg text-xs font-mono ${
                        t.active ? 'bg-amber-900/10 border border-amber-500/20' : 'opacity-50'
                      }`}>
                        <div className={`w-2 h-2 rounded-full mt-1 shrink-0 ${
                          t.level === 'P1' || t.level === '1' ? 'bg-red-500' : t.active ? 'bg-amber-500' : 'bg-gray-600'
                        }`} />
                        <div>
                          <span className="text-amber-400">{t.type || 'INCIDENT'}</span>
                          <span className="text-amber-800 ml-2">{Math.round(t.minutes_ago || 0)}m ago</span>
                          {t.address && <p className="text-amber-700 text-[10px]">{t.address}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Contacts Tab */}
          {tab === 'contacts' && (
            <div className="space-y-4">
              <p className="text-amber-700 text-xs font-mono">These people will be SMS-alerted with your location when you trigger SOS.</p>

              {contacts.map((c, i) => (
                <div key={i} className="flex items-center gap-3 p-3 bg-[#1a1025] rounded-lg border border-amber-900/20">
                  <PhoneCall size={16} className="text-emerald-500 shrink-0" />
                  <div className="flex-1">
                    <div className="text-amber-400 text-sm font-mono">{c.name}</div>
                    <div className="text-amber-700 text-xs font-mono">{c.phone}</div>
                  </div>
                  <span className="text-amber-800 text-[10px] font-mono uppercase">{c.relation}</span>
                </div>
              ))}

              {contacts.length < 5 && (
                <div className="space-y-2 p-3 bg-[#1a1025] rounded-lg border border-dashed border-amber-900/30">
                  <input
                    placeholder="Name"
                    value={newContact.name}
                    onChange={e => setNewContact(p => ({ ...p, name: e.target.value }))}
                    className="w-full bg-black/40 border border-amber-900/20 rounded-lg px-3 py-2 text-sm text-amber-400 font-mono placeholder:text-amber-900"
                  />
                  <input
                    placeholder="Phone (+1 520...)"
                    value={newContact.phone}
                    onChange={e => setNewContact(p => ({ ...p, phone: e.target.value }))}
                    className="w-full bg-black/40 border border-amber-900/20 rounded-lg px-3 py-2 text-sm text-amber-400 font-mono placeholder:text-amber-900"
                  />
                  <button
                    onClick={saveContact}
                    disabled={!newContact.name || !newContact.phone}
                    className="w-full py-2 bg-emerald-900/20 border border-emerald-500/30 rounded-lg text-emerald-400 font-mono text-xs disabled:opacity-30"
                  >
                    <Plus size={14} className="inline mr-1" /> Add Contact
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {tab === 'sos' && (
            <div className="space-y-4">
              {/* Shake to SOS toggle */}
              <div className="flex items-center justify-between p-3 bg-[#1a1025] rounded-lg border border-amber-900/20">
                <div>
                  <div className="text-amber-400 text-sm font-mono">Shake-to-SOS</div>
                  <div className="text-amber-800 text-[10px] font-mono">Shake phone 3x fast to trigger SOS</div>
                </div>
                <button
                  onClick={() => setShakeEnabled(!shakeEnabled)}
                  className={`w-12 h-6 rounded-full transition-all ${shakeEnabled ? 'bg-emerald-500' : 'bg-amber-900/30'}`}
                >
                  <div className={`w-5 h-5 rounded-full bg-white shadow transition-transform ${shakeEnabled ? 'translate-x-6' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {/* Ghost tier perk: free AIPetLocater */}
              {tier === 'ghost' && (
                <div className="p-3 bg-purple-900/20 border border-purple-500/30 rounded-lg">
                  <div className="text-purple-400 text-sm font-mono font-bold">🐕 GHOST PERK: Lost Pet Recovery</div>
                  <div className="text-purple-600 text-xs font-mono mt-1">
                    Your Ghost subscription includes FREE Rescue Pro tier ($39.99/mo value) at AIPetLocater.com.
                    AI-powered pet recovery with 24/7 scanning.
                  </div>
                  <a
                    href="https://aipetlocater.com?promo=GHOST-VIP"
                    target="_blank"
                    className="inline-block mt-2 px-4 py-1.5 bg-purple-600 text-white rounded-lg text-xs font-mono font-bold"
                  >
                    Activate Pet Recovery →
                  </a>
                </div>
              )}

              {/* Info */}
              <div className="p-3 bg-[#1a1025] rounded-lg border border-amber-900/20">
                <div className="text-amber-400 text-sm font-mono mb-2">How Safety Companion Works</div>
                <ul className="space-y-1 text-amber-700 text-[10px] font-mono">
                  <li>• AI analyzes live police data along your route</li>
                  <li>• Checks for active incidents within 2 miles</li>
                  <li>• SOS sends your GPS location to all emergency contacts via SMS</li>
                  <li>• Shake detection works even when screen is off</li>
                  <li>• Powered by Claude AI + live TPD dispatch data</li>
                </ul>
              </div>
            </div>
          )}

          {/* Session indicator */}
          {session && (
            <div className="mt-4 flex items-center justify-between p-3 bg-emerald-900/10 border border-emerald-500/20 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                <span className="text-emerald-500 text-xs font-mono">MONITORING ACTIVE</span>
              </div>
              <button
                onClick={() => {
                  fetch('/scanner-api/safety/end-session', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ sessionId: session, userId }),
                  });
                  setSession(null);
                  setAnalysis(null);
                }}
                className="text-amber-700 text-xs font-mono hover:text-amber-400"
              >
                End Session
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
